import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import SideMenu from '../components/SideMenu';
import { encryptId } from '../utils/urlEncryption';
import { 
  Menu, Package, TrendingUp, DollarSign, AlertCircle, 
  Eye, Edit, Trash2, Search, FileText, FileSpreadsheet, ArrowLeft,
  ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const OrderReports = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('current_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showOnlyRateBooked, setShowOnlyRateBooked] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all'); // all, orders, bookings
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  useEffect(() => {
    loadOrdersAndBookings();
  }, [dateRange, customStartDate, customEndDate]);

  // Helper functions defined BEFORE calculateStats to avoid hoisting issues
  const calculateCashAdvance = (order) => {
    if (!order.advance_payments) return 0;
    return order.advance_payments.reduce((sum, payment) => {
      if (payment.mode !== 'Metal Credit') {
        // Support both 'amount' (PaymentEntry model) and 'inr' (old format)
        const amount = parseFloat(payment.amount) || parseFloat(payment.inr) || 0;
        // Subtract refunds, add payments
        return payment.transaction_type === 'REFUND' ? sum - amount : sum + amount;
      }
      return sum;
    }, 0);
  };

  const calculateMetalAdvance = (order) => {
    if (!order.advance_payments) {
      return 0;
    }
    
    const totalMetal = order.advance_payments.reduce((sum, payment) => {
      if (payment.mode === 'Metal Credit') {
        // Use fine_wt which is the actual fine weight in grams
        const fineWeight = parseFloat(payment.fine_wt) || 0;
        return sum + fineWeight;
      }
      return sum;
    }, 0);
    
    return totalMetal;
  };

  const getPurityPercentage = (purityStr) => {
    if (!purityStr) return 0;
    const purity = purityStr.toString().toUpperCase();
    
    // Standard purity conversions
    const purityMap = {
      '24K': 100,
      '24': 100,
      '22K': 91.6,
      '22': 91.6,
      '18K': 75,
      '18': 75,
      '14K': 58.5,
      '14': 58.5,
      '10K': 41.7,
      '10': 41.7
    };
    
    return purityMap[purity] || 0;
  };

  const calculateTotalFineWeight = (order) => {
    if (!order.items || order.items.length === 0) {
      return 0;
    }
    
    return order.items.reduce((sum, item) => {
      let grossWeight = 0;
      
      // Get gross weight
      if (item.weight_type === 'Fixed' && item.weight_fixed) {
        grossWeight = parseFloat(item.weight_fixed);
      } else if (item.weight_type === 'Range') {
        const min = parseFloat(item.weight_min || 0);
        const max = parseFloat(item.weight_max || 0);
        grossWeight = (min + max) / 2;
      } else if (item.weight) {
        // For bookings - direct weight field
        grossWeight = parseFloat(item.weight);
      } else {
        grossWeight = parseFloat(item.gross_wt || item.net_wt || 0);
      }
      
      // Get purity percentage
      // For bookings without purity field, assume 24K (100%)
      const purityPercentage = item.purity ? getPurityPercentage(item.purity) : 100;
      
      // Calculate fine weight: gross × (purity% / 100)
      const fineWeight = grossWeight * (purityPercentage / 100);
      
      return sum + fineWeight;
    }, 0);
  };

  const calculateTotalWeight = (order) => {
    if (!order.items || order.items.length === 0) {
      return 0;
    }
    
    return order.items.reduce((sum, item) => {
      let weight = 0;
      
      if (item.weight_type === 'Fixed' && item.weight_fixed) {
        weight = parseFloat(item.weight_fixed);
      } else if (item.weight_type === 'Range') {
        const min = parseFloat(item.weight_min || 0);
        const max = parseFloat(item.weight_max || 0);
        weight = (min + max) / 2;
      } else if (item.weight) {
        // For bookings - direct weight field
        weight = parseFloat(item.weight);
      } else {
        weight = parseFloat(item.gross_wt || item.net_wt || 0);
      }
      
      return sum + weight;
    }, 0);
  };

  const loadOrdersAndBookings = async () => {
    setLoading(true);
    try {
      console.log('Starting to load orders and bookings...');

      // Fetch both orders and bookings with lightweight mode for better performance
      const [ordersRes, bookingsRes] = await Promise.all([
        axiosInstance.get('/orders', { params: { lightweight: true, limit: 500 } }),
        axiosInstance.get('/bookings', { params: { lightweight: true, limit: 500 } })
      ]);
      
      console.log(`Loaded ${ordersRes.data.length} orders and ${bookingsRes.data.length} bookings`);
      
      // OPTIMIZATION: Fetch ALL customers and production items ONCE
      let customersMap = {};
      let productionItemsMap = {};
      
      try {
        const customersRes = await axiosInstance.get('/customers', { params: { limit: 1000 } });
        
        // Create maps for quick lookup
        customersMap = customersRes.data.reduce((map, customer) => {
          map[customer.id] = customer;
          return map;
        }, {});
        console.log(`Loaded ${Object.keys(customersMap).length} customers`);
      } catch (err) {
        console.error('Error fetching customers:', err);
      }
      
      // Try to fetch production items separately (may not exist)
      try {
        const productionRes = await axiosInstance.get('/production/items');
        // Map production items by source_id (order_id)
        productionRes.data.forEach(item => {
          if (item.source_id && item.advance_status_message) {
            productionItemsMap[item.source_id] = item.advance_status_message;
          }
        });
      } catch (err) {
        // Production module may be removed, ignore this error
        console.log('Production items not available');
      }
      
      // Now enrich orders with customer and advance status data (no API calls in loop!)
      const ordersWithCustomers = ordersRes.data.map(order => {
        const customer = customersMap[order.customer_id] || {};
        return {
          ...order,
          type: 'order',
          customer_name: customer.name || 'N/A',
          customer_phone: customer.mobile || customer.phone || 'N/A',
          customer_phone_secondary: customer.mobile_secondary || customer.phone_secondary,
          advance_status_from_backend: productionItemsMap[order.order_id] || null
        };
      });
      
      // Process bookings similarly
      const bookingsWithCustomers = bookingsRes.data.map(booking => {
        const customer = customersMap[booking.customer_id] || {};
        return {
          ...booking,
          type: 'booking',
          customer_name: customer.name || 'N/A',
          customer_phone: customer.mobile || customer.phone || 'N/A'
        };
      });
      
      console.log('Setting orders and bookings state...');
      setOrders(ordersWithCustomers);
      setBookings(bookingsWithCustomers);
      console.log('Data loaded successfully!');
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Failed to load orders and bookings');
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  // Filter and search orders
  const filteredOrders = useMemo(() => {
    // Combine orders and bookings based on type filter
    let allData = [];
    if (typeFilter === 'all') {
      allData = [
        ...orders.map(o => ({ ...o, type: 'order' })),
        ...bookings.map(b => ({ ...b, type: 'booking' }))
      ];
    } else if (typeFilter === 'orders') {
      allData = orders.map(o => ({ ...o, type: 'order' }));
    } else if (typeFilter === 'bookings') {
      allData = bookings.map(b => ({ ...b, type: 'booking' }));
    }
    
    let filtered = [...allData];
    
    // Apply date range filter
    const now = new Date();
    let startDate, endDate;
    
    if (dateRange === 'today') {
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    } else if (dateRange === 'this_week') {
      // Last 7 days
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date();
    } else if (dateRange === 'current_month') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
      endDate = new Date();
    } else if (dateRange === 'custom' && customStartDate && customEndDate) {
      startDate = new Date(customStartDate);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999);
    }
    
    if (startDate && endDate) {
      filtered = filtered.filter(order => {
        // Use order_date for filtering (backdate support), fallback to created_at
        const dateToCheck = order.order_date || order.booking_date || order.created_at;
        if (!dateToCheck) return false;
        const orderDate = new Date(dateToCheck);
        return orderDate >= startDate && orderDate <= endDate;
      });
    }
    
    // Apply rate booked filter
    if (showOnlyRateBooked) {
      filtered = filtered.filter(order => {
        const ratePolicy = order.rate_policy || '';
        // Show only orders where rate is locked (lock-today)
        // Exclude: 'rate-at-delivery', 'none', null, or empty
        return ratePolicy === 'lock-today' && 
               (order.base24k_rate_ex_gst > 0 || order.rate_incl_gst_snapshot > 0);
      });
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(order => 
        order.order_id?.toLowerCase().includes(query) ||
        order.booking_id?.toLowerCase().includes(query) ||
        order.customer_name?.toLowerCase().includes(query) ||
        order.customer_phone?.includes(query)
      );
    }
    
    // Apply sorting if a column is selected
    if (sortColumn) {
      filtered.sort((a, b) => {
        let valueA, valueB;
        
        switch (sortColumn) {
          case 'date':
            valueA = new Date(a.order_date || a.booking_date || a.created_at || 0);
            valueB = new Date(b.order_date || b.booking_date || b.created_at || 0);
            break;
          
          case 'customer':
            valueA = (a.customer_name || '').toLowerCase();
            valueB = (b.customer_name || '').toLowerCase();
            break;
          
          case 'total_weight':
            valueA = calculateTotalWeight(a);
            valueB = calculateTotalWeight(b);
            break;
          
          case 'fine_weight':
            valueA = calculateTotalFineWeight(a);
            valueB = calculateTotalFineWeight(b);
            break;
          
          case '24k_weight':
            valueA = calculateTotalFineWeight(a);
            valueB = calculateTotalFineWeight(b);
            break;
          
          case 'advance_cash':
            valueA = calculateCashAdvance(a);
            valueB = calculateCashAdvance(b);
            break;
          
          case 'advance_metal':
            valueA = calculateMetalAdvance(a);
            valueB = calculateMetalAdvance(b);
            break;
          
          default:
            return 0;
        }
        
        // Handle comparison
        if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default sort by date - newest first (current date to previous date)
      filtered.sort((a, b) => {
        const dateA = new Date(a.order_date || a.booking_date || a.created_at || 0);
        const dateB = new Date(b.order_date || b.booking_date || b.created_at || 0);
        return dateB - dateA; // Descending order (newest first)
      });
    }
    
    return filtered;
  }, [orders, bookings, typeFilter, searchQuery, dateRange, customStartDate, customEndDate, showOnlyRateBooked, sortColumn, sortDirection]);

  // Calculate stats dynamically based on filtered data (for type-specific stats)
  const dynamicStats = useMemo(() => {
    const data = filteredOrders;
    const result = data.reduce((acc, item) => {
      acc.totalOrders += 1;
      acc.totalAdvanceCash += calculateCashAdvance(item);
      acc.totalMetalFine += calculateMetalAdvance(item);
      acc.totalFineWeight += calculateTotalFineWeight(item);
      
      const rate = item.base24k_rate_ex_gst || item.rate_incl_gst_snapshot || 0;
      if (rate > 0) {
        acc.totalRate += rate;
        acc.rateCount += 1;
      }
      
      return acc;
    }, { 
      totalOrders: 0, 
      totalAdvanceCash: 0, 
      totalMetalFine: 0, 
      totalFineWeight: 0,
      totalRate: 0,
      rateCount: 0
    });
    
    result.avgRate = result.rateCount > 0 ? result.totalRate / result.rateCount : 0;
    
    // Get type-specific label for the cards
    let typeLabel = 'Orders & Bookings';
    if (typeFilter === 'orders') {
      typeLabel = 'Orders';
    } else if (typeFilter === 'bookings') {
      typeLabel = 'Bookings';
    }
    result.typeLabel = typeLabel;
    
    return result;
  }, [filteredOrders, typeFilter]);

  const getAdvanceStatus = (order) => {
    // If we have the backend-calculated status, use it directly!
    if (order.advance_status_from_backend) {
      // Determine color based on message content
      const msg = order.advance_status_from_backend;
      let color = 'text-gray-500';
      if (msg.includes('Need')) {
        // Extract percentage to determine color
        const match = msg.match(/Adv: (\d+)%/);
        if (match) {
          const pct = parseInt(match[1]);
          color = pct < 40 ? 'text-red-600' : 'text-orange-500';
        }
      } else if (msg !== 'N/A') {
        color = 'text-green-600';
      }
      return { message: msg, color };
    }
    
    // Check if rate policy is "Rate at Delivery"
    if (order.rate_policy === 'Rate at Delivery') {
      return { message: 'N/A', color: 'text-gray-500' };
    }
    
    // For bookings - calculate estimated value based on weight and rate
    if (order.type === 'booking') {
      // If no rate is locked, show just the advance amount
      if (!order.base24k_rate_ex_gst || order.base24k_rate_ex_gst <= 0) {
        const totalAdvance = calculateCashAdvance(order);
        const metalAdvance = calculateMetalAdvance(order);
        const hasMetalCredit = metalAdvance > 0;
        
        if (totalAdvance === 0 && metalAdvance === 0) {
          return { message: 'No Advance', color: 'text-gray-500' };
        }
        
        const message = hasMetalCredit
          ? `Adv: ₹${totalAdvance.toLocaleString()} + ${metalAdvance.toFixed(2)}g metal`
          : `Adv: ₹${totalAdvance.toLocaleString()}`;
        return { message, color: 'text-blue-600' };
      }
      
      // If rate is locked, calculate estimated value
      let totalWeight = 0;
      if (order.items && order.items.length > 0) {
        totalWeight = order.items.reduce((sum, item) => sum + (parseFloat(item.weight) || 0), 0);
      }
      
      // Calculate estimated value: weight × rate (assume 24K)
      const rate = order.base24k_rate_ex_gst;
      let estimatedValue = totalWeight * rate;
      
      // Apply GST if exclusive
      if (order.gst_input_style === 'EXCL' && estimatedValue > 0) {
        estimatedValue = estimatedValue * 1.03;
      }
      
      if (estimatedValue === 0) {
        const totalAdvance = calculateCashAdvance(order);
        const metalAdvance = calculateMetalAdvance(order);
        const hasMetalCredit = metalAdvance > 0;
        
        if (totalAdvance === 0 && metalAdvance === 0) {
          return { message: 'No Advance', color: 'text-gray-500' };
        }
        
        const message = hasMetalCredit
          ? `Adv: ₹${totalAdvance.toLocaleString()} + ${metalAdvance.toFixed(2)}g metal`
          : `Adv: ₹${totalAdvance.toLocaleString()}`;
        return { message, color: 'text-blue-600' };
      }
      
      // Calculate advance percentage
      const totalAdvance = calculateCashAdvance(order);
      const metalAdvance = calculateMetalAdvance(order);
      const hasMetalCredit = metalAdvance > 0;
      const advancePercentage = estimatedValue > 0 ? (totalAdvance / estimatedValue) * 100 : 0;
      const requiredPercentage = order.advance_requirement_percentage || 80.0;
      const remainingPercentage = Math.max(0, requiredPercentage - advancePercentage);
      
      // Generate status message
      if (remainingPercentage > 0) {
        const message = hasMetalCredit 
          ? `Adv: ${Math.round(advancePercentage)}% (+ metal), Need: ${Math.round(remainingPercentage)}%`
          : `Adv: ${Math.round(advancePercentage)}%, Need: ${Math.round(remainingPercentage)}%`;
        return {
          message,
          color: advancePercentage < requiredPercentage * 0.5 ? 'text-red-600' : 'text-orange-500'
        };
      } else {
        const message = hasMetalCredit 
          ? `Adv: ${Math.round(advancePercentage)}% (+ metal)`
          : `Adv: ${Math.round(advancePercentage)}%`;
        return {
          message,
          color: 'text-green-600'
        };
      }
    }
    
    // For orders - EXACT SAME LOGIC AS BACKEND production items endpoint
    let totalAmount = order.total_amount || 0;
    
    // If total_amount is not set, calculate it from items (same as backend)
    if (!totalAmount || totalAmount <= 0) {
      if (order.items && order.items.length > 0) {
        let calculatedTotal = 0;
        for (const orderItem of order.items) {
          // Get weight
          let weight = 0;
          if (orderItem.weight_type === 'Fixed') {
            weight = parseFloat(orderItem.weight_fixed || 0);
          } else {
            const weightMin = parseFloat(orderItem.weight_min || 0);
            const weightMax = parseFloat(orderItem.weight_max || 0);
            weight = (weightMin + weightMax) / 2;
          }
          
          const quantity = parseFloat(orderItem.quantity || 1);
          let rate = parseFloat(orderItem.rate || 0);
          
          // Apply GST if exclusive (not for Kaccha)
          if (order.gst_input_style === 'EXCL' && order.tax_scope !== 'KACCHA' && rate > 0) {
            rate = rate * 1.03;
          }
          
          const making = parseFloat(orderItem.making_value || 0);
          // Weight is total weight for all pieces — quantity is informational only, NOT a multiplier
          let itemTotal;
          if (orderItem.making_type === 'WASTAGE') {
            let rate24k = parseFloat(order.base24k_rate_ex_gst || 0);
            if (order.gst_input_style === 'EXCL' && order.tax_scope !== 'KACCHA' && rate24k > 0) {
              rate24k = rate24k * 1.03;
            }
            itemTotal = weight * rate24k * (making / 100);
          } else {
            itemTotal = weight * rate;
            if (orderItem.making_type === 'PERCENT') {
              itemTotal += itemTotal * (making / 100);
            } else if (orderItem.making_type === 'PER_GRAM') {
              itemTotal += weight * making;
            } else {
              itemTotal += making; // FLAT
            }
          }
          calculatedTotal += itemTotal;
        }
        totalAmount = calculatedTotal;
      }
    }
    
    if (totalAmount === 0) {
      return { message: 'N/A', color: 'text-gray-500' };
    }
    
    // Calculate total advance (cash only, not metal)
    const totalAdvance = calculateCashAdvance(order);
    const metalAdvance = calculateMetalAdvance(order);
    const hasMetalCredit = metalAdvance > 0;
    
    // Calculate percentage
    const advancePercentage = totalAmount > 0 ? (totalAdvance / totalAmount) * 100 : 0;
    const requiredPercentage = order.advance_requirement_percentage || 80.0;
    const remainingPercentage = Math.max(0, requiredPercentage - advancePercentage);
    
    // Generate status message - same format as production workflow
    if (remainingPercentage > 0) {
      const message = hasMetalCredit 
        ? `Adv: ${Math.round(advancePercentage)}% (+ metal), Need: ${Math.round(remainingPercentage)}%`
        : `Adv: ${Math.round(advancePercentage)}%, Need: ${Math.round(remainingPercentage)}%`;
      return {
        message,
        color: advancePercentage < requiredPercentage * 0.5 ? 'text-red-600' : 'text-orange-500'
      };
    } else {
      const message = hasMetalCredit 
        ? `Adv: ${Math.round(advancePercentage)}% (+ metal)`
        : `Adv: ${Math.round(advancePercentage)}%`;
      return {
        message,
        color: 'text-green-600'
      };
    }
  };

  const handleDelete = async (id, type) => {
    const itemType = type === 'booking' ? 'booking' : 'order';
    if (!window.confirm(`Are you sure you want to delete this ${itemType}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      if (type === 'booking') {
        await axiosInstance.delete(`/bookings/${id}`);
        toast.success('Booking deleted successfully');
      } else {
        await axiosInstance.delete(`/orders/${id}`);
        toast.success('Order deleted successfully');
      }
      loadOrdersAndBookings();
    } catch (err) {
      console.error(`Error deleting ${itemType}:`, err);
      toast.error(`Failed to delete ${itemType}`);
    }
  };

  const exportToExcel = () => {
    try {
      // Prepare data for export
      const exportData = filteredOrders.map((order, idx) => {
        const advanceStatus = getAdvanceStatus(order);
        let rateType = 'None';
        const ratePolicy = order.rate_policy || '';
        
        if (ratePolicy === 'rate-at-delivery') {
          rateType = 'Rate at Delivery';
        } else if (ratePolicy === 'lock-today' || (ratePolicy !== 'none' && order.base24k_rate_ex_gst && order.base24k_rate_ex_gst > 0)) {
          rateType = 'Rate Booked';
        }
        
        return {
          'S.No': idx + 1,
          'Date': formatDate(order.order_date || order.booking_date || order.created_at),
          'Type': order.type === 'booking' ? 'Booking' : 'Order',
          'ID': order.order_id || order.booking_id,
          'Customer Name': order.customer_name || 'N/A',
          'Phone': order.customer_phone || 'N/A',
          'Total Weight (g)': calculateTotalWeight(order).toFixed(2),
          'Fine Weight (g)': calculateTotalFineWeight(order).toFixed(3),
          '24K Rate': order.base24k_rate_ex_gst && order.base24k_rate_ex_gst > 0 
            ? order.base24k_rate_ex_gst 
            : (order.rate_incl_gst_snapshot && order.rate_incl_gst_snapshot > 0 ? order.rate_incl_gst_snapshot : 'N/A'),
          'Advance Cash (₹)': calculateCashAdvance(order).toFixed(2),
          'Advance Metal (g)': calculateMetalAdvance(order).toFixed(3),
          'Rate Type': rateType,
          'Advance Status': advanceStatus.message
        };
      });
      
      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 6 },   // S.No
        { wch: 12 },  // Date
        { wch: 10 },  // Type
        { wch: 15 },  // ID
        { wch: 20 },  // Customer Name
        { wch: 15 },  // Phone
        { wch: 15 },  // Total Weight
        { wch: 15 },  // Fine Weight
        { wch: 12 },  // 24K Rate
        { wch: 18 },  // Advance Cash
        { wch: 18 },  // Advance Metal
        { wch: 18 },  // Rate Type
        { wch: 30 }   // Advance Status
      ];
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Orders Report');
      
      // Generate filename with date
      const fileName = `Orders_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Download file
      XLSX.writeFile(wb, fileName);
      
      toast.success('Excel file downloaded successfully!');
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      toast.error('Failed to export Excel file');
    }
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF('landscape');
      
      // Add title
      doc.setFontSize(16);
      doc.text('Orders Report', 14, 15);
      
      // Add date
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 14, 22);
      
      // Prepare table data
      const tableData = filteredOrders.map((order, idx) => {
        const advanceStatus = getAdvanceStatus(order);
        let rateType = 'None';
        const ratePolicy = order.rate_policy || '';
        
        if (ratePolicy === 'rate-at-delivery') {
          rateType = 'Rate at Delivery';
        } else if (ratePolicy === 'lock-today' || (ratePolicy !== 'none' && order.base24k_rate_ex_gst && order.base24k_rate_ex_gst > 0)) {
          rateType = 'Rate Booked';
        }
        
        // Format advance cash without special characters that cause issues
        const advanceCash = calculateCashAdvance(order);
        const advanceCashFormatted = advanceCash.toFixed(0);
        
        return [
          idx + 1,
          formatDate(order.order_date || order.booking_date || order.created_at),
          order.type === 'booking' ? 'Booking' : 'Order',
          order.order_id || order.booking_id,
          order.customer_name || 'N/A',
          calculateTotalWeight(order).toFixed(2),
          calculateTotalFineWeight(order).toFixed(3),
          order.base24k_rate_ex_gst && order.base24k_rate_ex_gst > 0 
            ? order.base24k_rate_ex_gst.toLocaleString('en-IN')
            : 'N/A',
          advanceCashFormatted, // Plain number without currency symbol
          calculateMetalAdvance(order).toFixed(3) + 'g',
          rateType,
          advanceStatus.message
        ];
      });
      
      // Add table with proper settings
      autoTable(doc, {
        head: [[
          'S.No',
          'Date',
          'Type',
          'ID',
          'Customer',
          'Total Wt (g)',
          'Fine Wt (g)',
          '24K Rate',
          'Adv Cash',
          'Adv Metal',
          'Rate Type',
          'Status'
        ]],
        body: tableData,
        startY: 28,
        styles: { 
          fontSize: 8, 
          cellPadding: 2,
          overflow: 'linebreak',
          cellWidth: 'wrap',
          valign: 'middle'
        },
        headStyles: { 
          fillColor: [251, 191, 36], 
          textColor: [0, 0, 0], 
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle'
        },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center', overflow: 'visible' }, // S.No - no wrap
          1: { cellWidth: 20, overflow: 'visible' }, // Date - no wrap
          2: { cellWidth: 16, halign: 'center', overflow: 'visible' }, // Type - no wrap
          3: { cellWidth: 24, overflow: 'visible' }, // ID - no wrap, wider
          4: { cellWidth: 28, overflow: 'linebreak' }, // Customer - CAN wrap
          5: { cellWidth: 16, halign: 'right', overflow: 'visible' }, // Total Weight - no wrap
          6: { cellWidth: 16, halign: 'right', overflow: 'visible' }, // Fine Weight - no wrap
          7: { cellWidth: 18, halign: 'right', overflow: 'visible' }, // 24K Rate - no wrap
          8: { cellWidth: 20, halign: 'right', overflow: 'visible' }, // Advance Cash - no wrap, wider
          9: { cellWidth: 18, halign: 'right', overflow: 'visible' }, // Advance Metal - no wrap
          10: { cellWidth: 22, overflow: 'visible' }, // Rate Type - no wrap
          11: { cellWidth: 38, overflow: 'visible' } // Status - no wrap, wider
        },
        alternateRowStyles: { fillColor: [254, 243, 199] },
        margin: { top: 28, left: 8, right: 8 }
      });
      
      // Generate filename with date
      const fileName = `Orders_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Download file
      doc.save(fileName);
      
      toast.success('PDF file downloaded successfully!');
    } catch (err) {
      console.error('Error exporting to PDF:', err);
      toast.error('Failed to export PDF file');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column - start with ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4 inline ml-1" /> : 
      <ArrowDown className="w-4 h-4 inline ml-1" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <SideMenu user={user} isOpen={menuOpen} onClose={() => setMenuOpen(false)} onLogout={onLogout} />
      
      <div className="page-container">
        {/* Header - Mobile Optimized */}
        <div className="page-header">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setMenuOpen(true)} 
              className="p-2 hover:bg-amber-100 rounded-xl transition-colors touch-target min-w-[44px] min-h-[44px]"
            >
              <Menu className="w-6 h-6 text-gray-700" />
            </button>
            <button 
              onClick={() => navigate(-1)} 
              className="p-2 hover:bg-amber-100 rounded-xl transition-colors min-w-[44px] min-h-[44px]"
              aria-label="Go back"
            >
              <ArrowLeft className="w-6 h-6 text-gray-700" />
            </button>
            <div>
              <h1 className="page-title">Order Reports</h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Comprehensive order analytics and reports</p>
            </div>
          </div>
        </div>

        {/* Summary Cards - Dynamic based on Type Filter - ADMIN ONLY - Standardized Amber Theme */}
        {(user.role === 'Admin' || user.role === 'Controller') && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-3 sm:p-4 lg:p-6 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">
                  Total {dynamicStats.typeLabel}
                </p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-amber-700 mt-1">{dynamicStats.totalOrders}</p>
              </div>
              <Package className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-amber-500 opacity-40" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-3 sm:p-4 lg:p-6 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Advance (Cash)</p>
                <p className="text-sm sm:text-lg lg:text-2xl font-bold text-green-700 mt-1">{formatCurrency(dynamicStats.totalAdvanceCash)}</p>
              </div>
              <DollarSign className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-green-500 opacity-40 hidden sm:block" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-3 sm:p-4 lg:p-6 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Fine Weight</p>
                <p className="text-sm sm:text-lg lg:text-2xl font-bold text-amber-700 mt-1">{dynamicStats.totalFineWeight.toFixed(2)}g</p>
              </div>
              <TrendingUp className="w-7 h-7 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-amber-500 opacity-40 hidden sm:block" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-lg sm:rounded-xl p-3 sm:p-4 lg:p-6 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm text-gray-600 font-medium">Metal (Fine)</p>
                <p className="text-lg lg:text-2xl font-bold text-amber-700 mt-1">{dynamicStats.totalMetalFine.toFixed(2)}g</p>
              </div>
              <AlertCircle className="w-8 h-8 lg:w-10 lg:h-10 text-amber-500 opacity-40 hidden sm:block" />
            </div>
          </div>

          <div className="col-span-2 lg:col-span-1 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-4 lg:p-6 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs lg:text-sm text-gray-600 font-medium">Avg 24K Rate</p>
                <p className="text-lg lg:text-2xl font-bold text-amber-700 mt-1">{formatCurrency(dynamicStats.avgRate)}</p>
              </div>
              <TrendingUp className="w-8 h-8 lg:w-10 lg:h-10 text-amber-500 opacity-40" />
            </div>
          </div>
        </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white rounded-xl p-4 mb-6 shadow-md">
          {/* Search - Full width on mobile */}
          <div className="mb-4 lg:mb-0 lg:hidden">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by ID, Customer, or Phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
                data-testid="mobile-search-input"
              />
            </div>
          </div>

          {/* Mobile Filter Row 1: Date & Type */}
          <div className="flex gap-2 mb-3 lg:hidden">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="flex-1 px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
              data-testid="mobile-date-filter"
            >
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="current_month">This Month</option>
              <option value="custom">Custom</option>
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="flex-1 px-3 py-2 text-sm bg-purple-50 border-2 border-purple-200 rounded-lg font-semibold text-purple-700"
              data-testid="mobile-type-filter"
            >
              <option value="all">All</option>
              <option value="orders">Orders</option>
              <option value="bookings">Bookings</option>
            </select>
          </div>

          {/* Mobile Custom Date Range */}
          {dateRange === 'custom' && (
            <div className="flex gap-2 mb-3 lg:hidden">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
              />
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
              />
            </div>
          )}

          {/* Mobile Filter Row 2: Rate Booked & Export */}
          <div className="flex gap-2 lg:hidden">
            <label className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 border-2 border-blue-200 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={showOnlyRateBooked}
                onChange={(e) => setShowOnlyRateBooked(e.target.checked)}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
              />
              <span className="text-xs font-semibold text-blue-700">Rate Booked</span>
            </label>

            <button
              onClick={exportToExcel}
              className="flex items-center justify-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg text-xs font-semibold"
              data-testid="mobile-export-excel"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Excel
            </button>

            <button
              onClick={exportToPDF}
              className="flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold"
              data-testid="mobile-export-pdf"
            >
              <FileText className="w-4 h-4" />
              PDF
            </button>
          </div>

          {/* Desktop Filter Layout */}
          <div className="hidden lg:flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Order ID, Customer Name, or Phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
                  data-testid="desktop-search-input"
                />
              </div>
            </div>

            {/* Date Range Filter */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
            >
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="current_month">Current Month</option>
              <option value="custom">Custom Range</option>
            </select>

            {dateRange === 'custom' && (
              <>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
                />
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-500 focus:outline-none"
                />
              </>
            )}

            {/* Type Filter */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 bg-purple-50 border-2 border-purple-200 rounded-lg text-sm font-semibold text-purple-700 cursor-pointer hover:bg-purple-100 transition-colors"
            >
              <option value="all">All (Orders + Bookings)</option>
              <option value="orders">Orders Only</option>
              <option value="bookings">Bookings Only</option>
            </select>

            {/* Rate Booked Filter */}
            <label className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-2 border-blue-200 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
              <input
                type="checkbox"
                checked={showOnlyRateBooked}
                onChange={(e) => setShowOnlyRateBooked(e.target.checked)}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
              />
              <span className="text-sm font-semibold text-blue-700">Rate Booked Only</span>
            </label>

            {/* Export Buttons */}
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Excel
            </button>

            <button
              onClick={exportToPDF}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <FileText className="w-4 h-4" />
              PDF
            </button>
          </div>
        </div>

        {/* Orders Table - Scrollable View Only */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No orders found</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-x-auto lg:overflow-hidden">
              <table className="w-full table-auto lg:table-fixed">
                <thead className="bg-gradient-to-r from-amber-100 to-orange-100">
                  <tr>
                    <th 
                      className="px-4 py-2 lg:px-1 text-left text-[10px] font-semibold text-gray-700 uppercase cursor-pointer hover:bg-amber-200 transition-colors lg:w-[7%]"
                      onClick={() => handleSort('date')}
                    >
                      Date {getSortIcon('date')}
                    </th>
                    <th className="px-4 py-2 lg:px-1 text-center text-[10px] font-semibold text-gray-700 uppercase lg:w-[4%]">Type</th>
                    <th className="px-4 py-2 lg:px-1 text-left text-[10px] font-semibold text-gray-700 uppercase lg:w-[10%]">ID</th>
                    <th 
                      className="px-4 py-2 lg:px-1 text-left text-[10px] font-semibold text-gray-700 uppercase cursor-pointer hover:bg-amber-200 transition-colors lg:w-[11%]"
                      onClick={() => handleSort('customer')}
                    >
                      Customer {getSortIcon('customer')}
                    </th>
                    <th 
                      className="px-4 py-2 lg:px-1 text-right text-[10px] font-semibold text-gray-700 uppercase cursor-pointer hover:bg-amber-200 transition-colors lg:w-[7%]"
                      onClick={() => handleSort('total_weight')}
                    >
                      Wt (g) {getSortIcon('total_weight')}
                    </th>
                    <th 
                      className="px-4 py-2 lg:px-1 text-right text-[10px] font-semibold text-gray-700 uppercase cursor-pointer hover:bg-amber-200 transition-colors lg:w-[7%]"
                      onClick={() => handleSort('fine_weight')}
                    >
                      Fine (g) {getSortIcon('fine_weight')}
                    </th>
                    <th className="px-4 py-2 lg:px-1 text-right text-[10px] font-semibold text-gray-700 uppercase lg:w-[7%]">Rate</th>
                    <th 
                      className="px-4 py-2 lg:px-1 text-right text-[10px] font-semibold text-gray-700 uppercase cursor-pointer hover:bg-amber-200 transition-colors lg:w-[9%]"
                      onClick={() => handleSort('advance_cash')}
                    >
                      Adv Cash {getSortIcon('advance_cash')}
                    </th>
                    <th 
                      className="px-4 py-2 lg:px-1 text-right text-[10px] font-semibold text-gray-700 uppercase cursor-pointer hover:bg-amber-200 transition-colors lg:w-[7%]"
                      onClick={() => handleSort('advance_metal')}
                    >
                      Metal {getSortIcon('advance_metal')}
                    </th>
                    <th className="px-4 py-2 lg:px-1 text-center text-[10px] font-semibold text-gray-700 uppercase lg:w-[8%]">Rate Type</th>
                    <th className="px-4 py-2 lg:px-1 text-left text-[10px] font-semibold text-gray-700 uppercase lg:w-[17%]">Status</th>
                    <th className="px-4 py-2 lg:px-1 text-center text-[10px] font-semibold text-gray-700 uppercase lg:w-[7%]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const advanceStatus = getAdvanceStatus(order);
                    // Determine rate type
                    let rateType = 'None';
                    const ratePolicy = order.rate_policy || '';
                    
                    if (ratePolicy === 'rate-at-delivery') {
                      rateType = 'Rate at Delivery';
                    } else if (ratePolicy === 'lock-today' || (ratePolicy !== 'none' && order.base24k_rate_ex_gst && order.base24k_rate_ex_gst > 0)) {
                      rateType = 'Rate Booked';
                    } else {
                      rateType = 'None';
                    }
                    
                    return (
                      <tr 
                        key={order.order_id || order.booking_id}
                        className="border-b border-gray-200 hover:bg-amber-50 transition-colors text-xs"
                      >
                        <td className="px-4 py-3 lg:px-1 lg:py-2 text-gray-700 whitespace-nowrap">{formatDate(order.order_date || order.booking_date || order.created_at)}</td>
                        <td className="px-4 py-3 lg:px-1 lg:py-2 text-center whitespace-nowrap">
                          <span className={`text-xs lg:text-[10px] font-bold px-2 py-1 lg:px-1.5 lg:py-0.5 rounded ${
                            order.type === 'booking' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            <span className="lg:hidden">{order.type === 'booking' ? 'Booking' : 'Order'}</span>
                            <span className="hidden lg:inline">{order.type === 'booking' ? 'BK' : 'OR'}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 lg:px-1 lg:py-2 whitespace-nowrap lg:whitespace-normal">
                          <button
                            onClick={() => {
                              const id = order.order_id || order.booking_id;
                              const encryptedId = encryptId(id);
                              if (order.type === 'booking') {
                                navigate(`/bookings/${encryptedId}`);
                              } else {
                                navigate(`/orders/${encryptedId}`);
                              }
                            }}
                            className={`text-xs font-semibold hover:underline cursor-pointer ${order.type === 'booking' ? 'text-purple-600 hover:text-purple-800' : 'text-blue-600 hover:text-blue-800'}`}
                          >
                            {order.order_id || order.booking_id}
                          </button>
                        </td>
                        <td className="px-4 py-3 lg:px-1 lg:py-2">
                          <div>
                            <p className="font-medium text-gray-800 whitespace-nowrap lg:whitespace-normal text-xs">{order.customer_name || 'N/A'}</p>
                            <p className="text-xs lg:text-[10px] text-gray-500 whitespace-nowrap lg:whitespace-normal">{order.customer_phone || 'N/A'}</p>
                            {order.customer_phone_secondary && (
                              <p className="text-xs text-gray-500 whitespace-nowrap lg:hidden">{order.customer_phone_secondary}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 lg:px-1 lg:py-2 text-right font-semibold text-gray-800 whitespace-nowrap">
                          {calculateTotalWeight(order).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 lg:px-1 lg:py-2 text-right font-semibold text-purple-700 whitespace-nowrap">
                          {calculateTotalFineWeight(order).toFixed(3)}
                        </td>
                        <td className="px-4 py-3 lg:px-1 lg:py-2 text-right text-gray-700 whitespace-nowrap">
                          {order.base24k_rate_ex_gst && order.base24k_rate_ex_gst > 0 
                            ? formatCurrency(order.base24k_rate_ex_gst) 
                            : (order.rate_incl_gst_snapshot && order.rate_incl_gst_snapshot > 0
                              ? formatCurrency(order.rate_incl_gst_snapshot)
                              : 'N/A')}
                        </td>
                        <td className="px-4 py-3 lg:px-1 lg:py-2 text-right font-semibold text-green-600 whitespace-nowrap">
                          {formatCurrency(calculateCashAdvance(order))}
                        </td>
                        <td className="px-4 py-3 lg:px-1 lg:py-2 text-right font-semibold text-purple-600 whitespace-nowrap">
                          {calculateMetalAdvance(order).toFixed(2)}g
                        </td>
                        <td className="px-4 py-3 lg:px-1 lg:py-2 text-center whitespace-nowrap">
                          <span className={`text-xs lg:text-[10px] font-medium px-2 py-1 lg:px-1 lg:py-0.5 rounded ${
                            rateType === 'Rate Booked' ? 'bg-green-100 text-green-700' : 
                            rateType === 'Rate at Delivery' ? 'bg-orange-100 text-orange-700' : 
                            'bg-gray-100 text-gray-600'
                          }`}>
                            <span className="lg:hidden">{rateType}</span>
                            <span className="hidden lg:inline">{rateType === 'Rate Booked' ? 'Booked' : rateType === 'Rate at Delivery' ? 'At Dlvry' : 'None'}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 lg:px-1 lg:py-2 whitespace-nowrap lg:whitespace-normal">
                          <span className={`text-xs lg:text-[10px] font-semibold ${advanceStatus.color}`}>
                            {advanceStatus.message}
                          </span>
                        </td>
                        <td className="px-4 py-3 lg:px-1 lg:py-2 whitespace-nowrap">
                          <div className="flex items-center justify-center gap-2 lg:gap-0.5">
                            <button
                              onClick={() => {
                                const id = order.type === 'booking' ? order.booking_id : order.order_id;
                                const encryptedId = encryptId(id);
                                navigate(order.type === 'booking' ? `/bookings/${encryptedId}` : `/orders/${encryptedId}`);
                              }}
                              className="p-2 lg:p-1 text-blue-600 hover:bg-blue-100 rounded-lg lg:rounded transition-colors"
                              title="View"
                            >
                              <Eye className="w-4 h-4 lg:w-3.5 lg:h-3.5" />
                            </button>
                            <button
                              onClick={() => {
                                const id = order.type === 'booking' ? order.booking_id : order.order_id;
                                const encryptedId = encryptId(id);
                                navigate(order.type === 'booking' ? `/bookings/edit/${encryptedId}` : `/orders/edit/${encryptedId}`);
                              }}
                              className="p-2 lg:p-1 text-green-600 hover:bg-green-100 rounded-lg lg:rounded transition-colors"
                              title={order.type === 'booking' ? 'Edit Booking' : 'Edit Order'}
                            >
                              <Edit className="w-4 h-4 lg:w-3.5 lg:h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(order.order_id || order.booking_id, order.type)}
                              className="p-2 lg:p-1 text-red-600 hover:bg-red-100 rounded-lg lg:rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 lg:w-3.5 lg:h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderReports;
